import { useCallback, useEffect, useMemo, useState } from "react";
import * as api from "../../../utils/api/deliveryDocumentation";
import {
  EVIDENCE_TYPES,
  OUTCOME_LABELS,
  conclusionLabel,
  conclusionReasonLabel,
} from "../../../utils/cashier/deliveryDocumentation";
import AuditTimeline from "./AuditTimeline";
import { Button, XCircle } from "../../ui";
import { RetryButton } from "./AdminUi";
import { useAppLabel } from "../../../utils/useAppLabel";

function Field({ label, value }) {
  return (
    <div className="admin-detail-cell">
      <span>{label}</span>
      <b>{value == null || value === "" ? "غير متوفر" : String(value)}</b>
    </div>
  );
}

function EvidenceTile({ type, file, state }) {
  return (
    <div className={`admin-evidence-tile ${state || ""}`}>
      {file?.url ? (
        <img src={file.url} alt={type.label} />
      ) : (
        <div className="admin-evidence-empty">لا يوجد ملف</div>
      )}
      <div className="cap">
        <b>{type.label}</b>
        <span>
          {state === "missing"
            ? "مطلوب ومفقود"
            : state === "optional"
              ? "اختياري"
              : "حالي"}
        </span>
      </div>
    </div>
  );
}

export default function AdminOrderDetails({ orderId, onClose }) {
  const appLabel = useAppLabel();
  const [state, setState] = useState({ loading: true, error: "", order: null });
  const [historyState, setHistoryState] = useState({
    loading: true,
    error: "",
    items: [],
  });
  const load = useCallback(async () => {
    if (!orderId) return;
    setState({ loading: true, error: "", order: null });
    setHistoryState({ loading: true, error: "", items: [] });
    try {
      const [details, history] = await Promise.all([
        api.getAdminDocumentationOrder(orderId),
        api.getAdminDocumentationOrderHistory(orderId),
      ]);
      setState({ loading: false, error: "", order: details.order });
      setHistoryState({
        loading: false,
        error: "",
        items: history.items || [],
      });
    } catch (error) {
      setState({ loading: false, error: error.message, order: null });
      setHistoryState((value) => ({ ...value, loading: false }));
    }
  }, [orderId]);
  useEffect(() => {
    load();
  }, [load]);

  const evidence = useMemo(() => state.order?.evidence || {}, [state.order]);
  const evidenceSets = useMemo(() => {
    const required = new Set(state.order?.requiredEvidenceTypes || []);
    return {
      required: EVIDENCE_TYPES.filter((item) => required.has(item.key)),
      present: EVIDENCE_TYPES.filter((item) => evidence[item.key]?.url),
      missing: EVIDENCE_TYPES.filter(
        (item) => required.has(item.key) && !evidence[item.key]?.url,
      ),
      optionalPresent: EVIDENCE_TYPES.filter(
        (item) => !required.has(item.key) && evidence[item.key]?.url,
      ),
    };
  }, [state.order, evidence]);

  if (!orderId) return null;
  return (
    <section className="admin-details-panel">
      <div className="admin-details-top">
        <div>
          <h2>تفاصيل الطلب</h2>
          <p>الحالة الحالية مع السجل الكامل والبيانات المحذوفة أو المستبدلة.</p>
        </div>
        <Button 
            variant="danger"
            appearance="soft" type="button" onClick={onClose} icon={<XCircle size={13}/>}/>
      </div>
      {state.loading && (
        <div className="admin-empty">جاري تحميل تفاصيل الطلب…</div>
      )}
      {state.error && (
        <div className="admin-empty admin-error">
          {state.error}{" "}
          <RetryButton onClick={load} />
        </div>
      )}
      {state.order && (
        <>
          <div className="admin-detail-grid">
            <Field label="المطعم" value={state.order.storeSnapshot?.name} />
            <Field label="الفرع" value={state.order.branchSnapshot?.name} />
            <Field
              label="التطبيق"
              value={appLabel(
                state.order.applicationSnapshot?.app,
                state.order.applicationSnapshot?.name,
              )}
            />
            <Field label="آخر أربعة أرقام" value={`#${state.order.last4}`} />
            <Field label="تاريخ العمل" value={state.order.businessDate} />
            <Field
              label="الكاشير"
              value={state.order.cashier?.name || state.order.cashier?.email}
            />
            <Field
              label="حالة التوثيق"
              value={
                state.order.documentationStatus === "complete"
                  ? "مكتمل"
                  : "قيد التوثيق"
              }
            />
            <Field
              label="الحالة الخاصة"
              value={OUTCOME_LABELS[state.order.outcome] || state.order.outcome}
            />
            <Field
              label="المندوب لم يستلم"
              value={state.order.courierNotPickedUp ? "نعم" : "لا"}
            />
            <Field
              label="حالة المطابقة"
              value={state.order.matchStatus || "غير مرتبطة"}
            />
            {state.order.deletedAt && (
              <Field
                label="حذف بواسطة"
                value={
                  state.order.deletedByUser?.name ||
                  state.order.deletedByUser?.email
                }
              />
            )}
            {state.order.deletedAt && (
              <Field
                label="وقت الحذف"
                value={new Date(state.order.deletedAt).toLocaleString("ar-SA")}
              />
            )}
          </div>
          {state.order.deleteReason && (
            <div className="admin-note danger">
              سبب الحذف: {state.order.deleteReason}
            </div>
          )}
          {state.order.notes && (
            <div className="admin-note">{state.order.notes}</div>
          )}
          {!!state.order.conclusions?.length && (
            <div className="admin-note">
              {state.order.conclusions.map((item) => (
                <div key={item._id}>
                  {conclusionLabel(item.type)} ·{" "}
                  {conclusionReasonLabel(item.type, item.reason)}
                  {item.revertedAt ? " · تم التراجع" : ""}
                </div>
              ))}
            </div>
          )}
          {!!state.order.outcomeNotes?.length && (
            <div className="admin-note">
              {state.order.outcomeNotes.map((note) => (
                <div key={note._id}>{note.text}</div>
              ))}
            </div>
          )}
          <div className="admin-section-title">الأدلة الحالية</div>
          <div className="admin-photo-grid">
            {EVIDENCE_TYPES.map((type) => {
              const required = state.order.requiredEvidenceTypes?.includes(
                type.key,
              );
              return (
                <EvidenceTile
                  key={type.key}
                  type={type}
                  file={evidence[type.key]}
                  state={
                    evidence[type.key]?.url
                      ? "current"
                      : required
                        ? "missing"
                        : "optional"
                  }
                />
              );
            })}
          </div>
          <div className="admin-evidence-summary">
            <span>
              مطلوب:{" "}
              {evidenceSets.required.map((item) => item.label).join("، ") ||
                "لا يوجد"}
            </span>
            <span>
              موجود:{" "}
              {evidenceSets.present.map((item) => item.label).join("، ") ||
                "لا يوجد"}
            </span>
            <span>
              مفقود:{" "}
              {evidenceSets.missing.map((item) => item.label).join("، ") ||
                "لا يوجد"}
            </span>
          </div>
          <div className="admin-section-title">الأدلة المحذوفة والمستبدلة</div>
          <AuditTimeline
            items={historyState.items.filter(
              (item) =>
                item.action === "EVIDENCE_DELETED" ||
                item.action === "EVIDENCE_REPLACED",
            )}
            loading={historyState.loading}
            error={historyState.error}
            onRetry={load}
          />
          <div className="admin-section-title">السجل الكامل</div>
          <AuditTimeline {...historyState} onRetry={load} />
        </>
      )}
    </section>
  );
}
